import { create } from "zustand";
import { produce } from "immer";

// Used by the form for subdomain handling
interface SubdomainRequest {
  prefix: string;
  fullDomain: string;
}

// Matches the structure used in NewGcpCerts.tsx
export interface NewGcpCertFormType {
  application_alias: string;
  environment: string;
  certificate_type: string;
  platform_name: string;
  project_id: string;
  application_owner: string;
  application_owner_email: string;
  wbs_code: string;
  cost_center: string;
  dns_domain_name: string;
  subdomain_requests: SubdomainRequest[];
  envOptions?: { label: string; value: string }[]; // dynamically updated list
  [key: string]: any;
}

interface NewGcpCertsFormState {
  isFormValid: boolean;
  formValues: NewGcpCertFormType;
}

interface NewGcpCertsFormActions {
  actions: {
    clearFormValues: () => void;
    setFormValues: <K extends keyof NewGcpCertFormType>(
      fieldName: K,
      value: NewGcpCertFormType[K]
    ) => void;
    setFormValidity: (isValid: boolean) => void;
  };
}

const initialFormValues: NewGcpCertFormType = {
  application_alias: "",
  environment: "",
  certificate_type: "",
  platform_name: "",
  project_id: "",
  application_owner: "",
  application_owner_email: "",
  wbs_code: "",
  cost_center: "",
  dns_domain_name: "",
  subdomain_requests: [],
  envOptions: [],
};

// Zustand store
const newGcpCertsFormStore = create<
  NewGcpCertsFormState & NewGcpCertsFormActions
>((set) => ({
  isFormValid: false,
  formValues: initialFormValues,
  actions: {
    clearFormValues: () =>
      set(() => ({
        formValues: initialFormValues,
        isFormValid: false,
      })),

    setFormValidity: (isValid: boolean) =>
      set(() => ({
        isFormValid: isValid,
      })),

    setFormValues: (fieldName, value) =>
      set(
        produce((state: NewGcpCertsFormState) => {
          // Handle Select components (store only the value)
          if (value && typeof value === "object" && "value" in value) {
            state.formValues[fieldName] = value.value;
          } else {
            state.formValues[fieldName] = value;
          }
        })
      ),
  },
}));

export const useNewGcpCertsFormStore = () =>
  newGcpCertsFormStore((state) => state);